import {
  EntityTypes,
  SoftDeleteFlag,
  Timestamps,
  Versioned,
} from './shared.types';
import { PaymentMethodType } from './payment-types.types';

export interface WalletMessage {
  id: string;
  content: string;
  created_at: string;
  receiver_number: string;
}

export interface WalletMessageDBItem extends WalletMessage {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

export interface GetWalletMessage {
  id: string;
}

export interface GetAllWalletMessages {
  by_date_range?: boolean;
  by_receiver_number?: boolean;
  start_date?: string;
  end_date?: string;
  receiver_number?: string;
  count?: number;
  cursor?: string;
}

export interface WalletMessagesPage {
  count?: number;
  cursor?: string;
  wallet_messages: WalletMessage[];
}

export enum MessageOperator {
  EMOLA = 'EMOLA',
  MPESA = 'MPESA',
  BIM = 'BIM',
  MTN = 'MTN',
  AIRTEL = 'AIRTEL',
  TIGO = 'TIGO',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Two-letter prefix at the start of an e-Mola transaction ID.
 * Determines how the message body should be interpreted.
 */
export enum EmolaPrefix {
  PP = 'PP', // Person-to-Person transfer (IN or OUT)
  CI = 'CI', // Cash-In (IN: agent deposit, mPesa SIMO, BIM SIMO)
  CO = 'CO', // Cash-Out (OUT: SIMO transfer, agent withdrawal, utility, POS)
  BP = 'BP', // Bill Payment (OUT: Credelec, water, etc.)
  RC = 'RC', // Recharge (OUT: own airtime)
  TC = 'TC', // Transaction Cancelled / Reversal
  OT = 'OT', // POS withdrawal authorization token (non-financial)
  EM = 'EM', // Balance snapshot / app event (non-financial)
}

/**
 * Fine-grained classification of a parsed transaction.
 */
export enum TxSubtype {
  P2P = 'P2P',
  AGENT_DEPOSIT = 'AGENT_DEPOSIT',
  BANK_FUNDED = 'BANK_FUNDED',
  SIMO_TO_MPESA = 'SIMO_TO_MPESA',
  SIMO_TO_BIM = 'SIMO_TO_BIM',
  AGENT_WITHDRAWAL = 'AGENT_WITHDRAWAL',
  UTILITY_PAYMENT = 'UTILITY_PAYMENT',
  POS_PAYMENT = 'POS_PAYMENT',
  CREDELEC = 'CREDELEC',
  WATER = 'WATER',
  AIRTIME_RECHARGE = 'AIRTIME_RECHARGE',
  REVERSAL = 'REVERSAL',
  POS_AUTHORIZATION = 'POS_AUTHORIZATION',
  BALANCE_SNAPSHOT = 'BALANCE_SNAPSHOT',
}

/**
 * Type of counterparty extracted from the SMS body.
 */
export enum CounterpartyType {
  MSISDN = 'MSISDN',
  AGENT = 'AGENT',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  MERCHANT = 'MERCHANT',
  METER = 'METER',
  SELF = 'SELF',
}

export enum TopupTransactionType {
  MONEY_IN = 'MONEY_IN',
  MONEY_OUT = 'MONEY_OUT',
}

export enum ClaimStatus {
  UNCLAIMED = 'UNCLAIMED',
  CLAIMED_AUTO = 'CLAIMED_AUTO',
  CLAIMED_MANUAL = 'CLAIMED_MANUAL',
  REVOKED = 'REVOKED',
  REJECTED = 'REJECTED',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
}

export enum AssignmentSource {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL',
  FALLBACK_DETAILS = 'FALLBACK_DETAILS',
  AI_EXTRACTED = 'AI_EXTRACTED',
}

export enum ClaimHistoryTypes {
  SUCCESS = 'SUCCESS',
  TRANSACTION_ID_NOT_FOUND = 'TRANSACTION_ID_NOT_FOUND',
  AMOUNT_NOT_EQUAL = 'AMOUNT_NOT_EQUAL',
  DATE_TOO_OLD = 'DATE_TOO_OLD',
  ALREADY_CLAIMED = 'ALREADY_CLAIMED',
  DATE_MISSING = 'DATE_MISSING',
  SUSPICIOUS_MISMATCH = 'SUSPICIOUS_MISMATCH',
  REVERSED = 'REVERSED',
  // Event-style entries appended by the claims/* use-cases.
  CLAIM_SUBMITTED = 'CLAIM_SUBMITTED',
  AUTO_CLAIM_SUCCESS = 'AUTO_CLAIM_SUCCESS',
  AUTO_CLAIM_FAILED = 'AUTO_CLAIM_FAILED',
  MANUAL_APPROVED = 'MANUAL_APPROVED',
  MANUAL_REJECTED = 'MANUAL_REJECTED',
  WALLET_CREDIT_SUCCESS = 'WALLET_CREDIT_SUCCESS',
  WALLET_CREDIT_FAILED = 'WALLET_CREDIT_FAILED',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  REVERSAL_DETECTED = 'REVERSAL_DETECTED',
  // AI extraction outcomes
  AI_EXTRACTION_FAILED = 'AI_EXTRACTION_FAILED',
  AI_NOT_A_TRANSACTION = 'AI_NOT_A_TRANSACTION',
  AI_GIBBERISH = 'AI_GIBBERISH',
  AI_UNRECOGNIZED_FORMAT = 'AI_UNRECOGNIZED_FORMAT',
  AI_TIMEOUT = 'AI_TIMEOUT',
  AI_AMOUNT_MISMATCH = 'AI_AMOUNT_MISMATCH',
  AI_PARSING_CORRECTED = 'AI_PARSING_CORRECTED',
  // Admin review actions
  MANUAL_REVOKED = 'MANUAL_REVOKED',
  REVOKE_FAILED = 'REVOKE_FAILED',
  REOPENED = 'REOPENED',
  CORRECTED = 'CORRECTED',
}

/**
 * How a `TopupRequest` was matched to a claim, recorded on the request when it
 * transitions from `UNCLAIMED`. Free-form for forward-compatibility.
 */
export enum TopupClaimMatchMethod {
  TRANSACTION_ID = 'TRANSACTION_ID',
  AMOUNT_AND_TIME = 'AMOUNT_AND_TIME',
  AMOUNT_TIME_AND_SENDER = 'AMOUNT_TIME_AND_SENDER',
  AMOUNT_TIME_AND_RECEIVER = 'AMOUNT_TIME_AND_RECEIVER',
  AMOUNT_TIME_AND_OPERATOR = 'AMOUNT_TIME_AND_OPERATOR',
  MANUAL_APPROVAL = 'MANUAL_APPROVAL',
}

export interface ClaimHistory {
  by: string;
  entity_type: EntityTypes;
  created_at: string;
  type: ClaimHistoryTypes;
  /** Free-form context for the history entry (error message, reviewer comment, …). */
  note?: string;
}

export interface TopupRequest {
  receiver_number: string;
  sender_contact: string;
  operator: MessageOperator;
  message_content: string;
  transaction_id: string;
  amount: number;
  transaction_type: TopupTransactionType;
  claim_status: ClaimStatus;
  claim_history: ClaimHistory[];
  created_at: string;
  /** Optional for backwards compatibility; missing on rows never updated. */
  updated_at?: string;
  /** Optional for backwards compatibility; missing = 0. */
  version?: number;
  /** Optional for backwards compatibility; missing on older rows. */
  updated_by_id?: string;
  /** Optional for backwards compatibility; missing on older rows. */
  updated_by_name?: string;
  assignment_source?: AssignmentSource;
  claimed_at?: string;
  merchant_id?: string;
  store_id?: string;
  claimed_by?: string;
  client_claim_text?: string;
  entity_type?: EntityTypes;
  wallet_balance?: number;
  /** Original transaction ID if this topup was reversed by a TC message. */
  reversed_by?: string;
  /** ISO date the reversal was applied. */
  reversed_at?: string;

  // ----- Wallet credit reference (populated once Wallets credits the merchant) -----
  /** Reference type sent to the Wallets API (always `topup_request`). */
  wallet_credit_reference_type?: string;
  /** Reference id sent to the Wallets API (always the `transaction_id`). */
  wallet_credit_reference_id?: string;
  /** Monotonic wallet transaction version returned by Wallets. */
  wallet_credit_transaction_version?: number;
  /** ISO date Wallets recorded the credit. */
  wallet_credit_created_at?: string;
  /** Wallet snapshot balance returned by Wallets after the credit. */
  wallet_credit_balance_after?: number;
  /** Last error message returned by the Wallets API when a credit attempt failed. */
  wallet_credit_error?: string;

  // ----- Claim matching / review attribution -----
  /** How the request was matched to the claim. */
  claim_match_method?: TopupClaimMatchMethod;
  /** Optional matcher score (0..1). Reserved for future scoring extensions. */
  claim_match_score?: number;
  /** Reason the request was placed in `NEEDS_REVIEW`. */
  claim_review_reason?: string;
  /** ISO date the request was put into manual review. */
  manual_review_requested_at?: string;

  // ----- Payment type used for the wallet credit -----
  /** The PaymentType.id used when crediting the wallet for this claim. */
  wallet_credit_payment_type_id?: string;
  /** The PaymentType.name snapshot used when crediting the wallet. */
  wallet_credit_payment_type_name?: string;
  /** The PaymentMethodType snapshot used when crediting the wallet. */
  wallet_credit_payment_type?: PaymentMethodType;
  /**
   * Merchant whose wallet a claim in progress is crediting, written before
   * the credit. Lets a claim that died mid-way be recovered against the
   * right wallet. Optional for backwards compatibility; missing on older rows.
   */
  claim_merchant_id?: string;

  // ----- Rejection attribution -----
  /** ISO date the request was rejected. */
  rejected_at?: string;
  /** Id of the principal that rejected the request. */
  rejected_by?: string;
  /** Operator-provided rejection reason. */
  rejection_reason?: string;

  // ----- Revocation attribution (optional; missing = never revoked) -----
  /** ISO date the claim was revoked and its wallet credit taken back. */
  revoked_at?: string;
  /** Id of the principal that revoked the claim. */
  revoked_by?: string;
  /** Operator-provided revocation reason. */
  revocation_reason?: string;

  // ----- Ledger details (optional; missing on rows created before IFTTT ingestion) -----
  /** Operator fee charged on this transaction. Missing = unknown (treat as 0). */
  fee?: number;
  /** Account balance reported by the SMS after this transaction. */
  balance_after?: number;
  /** Counterparty phone/account number reported by the SMS. */
  counterparty_number?: string;
  /** Counterparty display name reported by the SMS. */
  counterparty_name?: string;
  /** Free-text note ("Content: ...") typed by the sender. */
  content?: string;
  /** When the transaction happened (ISO UTC). Missing = use `created_at`. */
  occurred_at?: string;
  /** Where the SMS came from. Missing = SMS_READER (legacy app). */
  source?: TopupSource;
  /** IFTTT DeviceLabel of the phone that received the SMS. */
  device_label?: string;
}

/** Origin of a mobile-money SMS ingested by service-topups. */
export enum TopupSource {
  SMS_READER = 'SMS_READER',
  IFTTT = 'IFTTT',
}

/**
 * @deprecated Topups no longer issues wallet credits directly. Use the new
 * `claim-topup-by-*` / `approve-topup-claim` endpoints which call the Wallets
 * service via `ShopAndSmileAPIAdapter.addMoneyToWallet`.
 */
export interface TopupWallet {
  operator: MessageOperator;
  transaction_id?: string;
  amount: number;
  assignment_source: AssignmentSource;
  merchant_id: string;
  store_id: string;
  claimed_by: string;
  client_claim_text?: string;
  entity_type: EntityTypes;
  date?: string;
}

export interface TopupRequestDBItem extends TopupRequest {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  // Namespaced GSI columns added to support claim-by-details matching.
  // Older rows may not have these columns; the repository falls back to
  // EntityIndex queries when they are absent.
  GSI1PK?: string; // TOPUP_AMOUNT#<amount>
  GSI1SK?: string; // DATE#<created_at>
  GSI2PK?: string; // TOPUP_RECEIVER#<receiver_number>
  GSI2SK?: string; // DATE#<created_at>
  GSI3PK?: string; // TOPUP_SENDER#<sender_contact>
  GSI3SK?: string; // DATE#<created_at>
}

export interface CreateTopupRequest {
  receiver_number: string; // phone number receiving the top-up, the number the Mobile Money is sent to
  sender_contact: string; // phone number/contact of the person/company sending the top-up message
  password: string; // admin password to authenticate the request
  message_content: string;
}

export interface GetTopupRequest {
  transaction_id: string;
}

export interface GetAllTopupRequests {
  by_claim_status?: boolean;
  by_merchant?: boolean;
  by_receiver_number?: boolean;
  by_operator?: boolean;
  by_date_range?: boolean;
  by_amount_range?: boolean;
  by_transaction_type?: boolean;
  by_sender_contact?: boolean;

  claim_status?: ClaimStatus;
  merchant_id?: string;
  receiver_number?: string;
  operator?: MessageOperator;
  start_date?: string;
  end_date?: string;
  start_amount?: number;
  end_amount?: number;
  transaction_type?: TopupTransactionType;
  sender_contact?: string;

  password?: string; // only when the phone number is provided

  /** Optional; filters on `store_id` (needs `by_store`). */
  by_store?: boolean;
  store_id?: string;
  /**
   * Optional; missing = false (oldest first, `count` rows read before the
   * filters). When true: newest first, and `count` is the number of matching
   * rows returned (fewer only at the end).
   */
  newest_first?: boolean;

  count?: number;
  cursor?: string;
}

export interface TopupRequestsPage {
  count?: number;
  cursor?: string;
  topup_requests: TopupRequest[];
}

export interface TopupPassword extends Versioned {
  current_password: string;
  updated_at: string;
  update_history: TopupPasswordUpdateHistory[];
  phone_number: string;
}

export interface TopupPasswordUpdateHistory {
  updated_at: string;
  old_password: string;
}

export interface TopupPasswordDBItem extends TopupPassword {
  SK: string; // TOPUP_PASSWORD
  PK: string; // TOPUP_NUMBER#phone_number
  EntityIndexPK: string; // TOPUP_PASSWORD
  EntityIndexSK: string; // DATE#updated_at
}

export interface UpdateTopupPassword {
  old_password: string;
  new_password: string;
  phone_number: string;
}

export enum WalletTransferDirection {
  IN = 'IN',
  OUT = 'OUT',
  NOISE = 'NOISE',
}

export interface WalletTransferOut {
  transaction_id: string;
  amount: number;
  recipient_number: string;
  recipient_name: string;
  fee: number;
  balance_after: number;
  message_content: string;
  receiver_number: string;
  created_at: string;
  /** Fine-grained OUT classification (missing on legacy rows). */
  subtype?: TxSubtype;
  /** Shape of the counterparty captured (missing on legacy rows). */
  counterparty_type?: CounterpartyType;
  /** Original transaction ID if this transfer was reversed by a TC message. */
  reversed_by?: string;
  /** ISO date the reversal was applied. */
  reversed_at?: string;
  /** Free-text note ("Content: ...") typed in the transfer. */
  content?: string;
  /** When the transaction happened (ISO UTC). Missing = use `created_at`. */
  occurred_at?: string;
  /** Where the SMS came from. Missing = SMS_READER (legacy app). */
  source?: TopupSource;
  /** Mobile-money operator. Missing = EMOLA (legacy rows). */
  operator?: MessageOperator;
}

export interface WalletTransferOutDBItem extends WalletTransferOut {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  GSI1PK: string;
  GSI1SK: string;
  GSI2PK: string;
  GSI2SK: string;
  GSI3PK: string;
  GSI3SK: string;
}

export interface WalletGetTransferOut {
  transaction_id: string;
}

export interface WalletGetAllTransferOuts {
  by_recipient_number?: boolean;
  by_receiver_number?: boolean;
  by_amount?: boolean;
  by_date_range?: boolean;

  recipient_number?: string;
  receiver_number?: string;
  amount?: number;
  start_date?: string;
  end_date?: string;

  count?: number;
  cursor?: string;
}

export interface WalletTransferOutsPage {
  count?: number;
  cursor?: string;
  transfer_outs: WalletTransferOut[];
}

export interface WalletHandleSmsResponse {
  direction: WalletTransferDirection;
  topup_request: TopupRequest | null;
  transfer_out: WalletTransferOut | null;
  /** True when the message was recognized as OTP/promo/token/etc. */
  noise?: boolean;
  /** True when the message had a valid-looking ID but no body pattern matched. */
  unparsed?: boolean;
}

export enum TopupNumberStatus {
  ACTIVE = 'ACTIVE',
  /** Auto-created from an IFTTT SMS; the real phone number is not known yet. */
  PENDING_SETUP = 'PENDING_SETUP',
}

export interface TopupNumber extends Timestamps, Versioned {
  /**
   * The account number. For PENDING_SETUP numbers this is a synthetic key
   * `device:<device-label>:<operator>` until the real number is known.
   */
  phone_number: string;
  location: string;
  platform_payment_type_id: string;
  update_history: TopupNumberUpdateHistory;
  /** Mobile-money operator of this account. Optional for backwards compatibility. */
  operator?: MessageOperator;
  /** IFTTT DeviceLabel of the phone holding this SIM. Optional. */
  device_label?: string;
  /** Missing = ACTIVE (legacy rows). */
  status?: TopupNumberStatus;
  /** Set on a PENDING_SETUP number once promoted: the real phone number. */
  merged_into?: string;
}

export type TopupNumberUpdateHistory = {
  updated_at: string;
  phone_number: string;
  location: string;
  platform_payment_type_id?: string;
  operator?: MessageOperator;
  device_label?: string;
}[];

export interface TopupNumberDBItem extends TopupNumber {
  SK: string; // TOPUP_NUMBER#phone_number
  PK: string; // TOPUP_NUMBER
  EntityIndexPK: string; // TOPUP_NUMBER
  EntityIndexSK: string; // DATE#created_at
  GSI1PK?: string; // PLATFORM_PAYMENT_TYPE#{platform_payment_type_id}
  GSI1SK?: string; // DATE#created_at
}

export interface CreateTopupNumber {
  phone_number: string;
  location: string;
  password: string;
  /** The PlatformPaymentType.id this number belongs to. Required. */
  platform_payment_type_id: string;
  operator?: MessageOperator;
  /** IFTTT DeviceLabel of the phone holding this SIM (unique with operator). */
  device_label?: string;
}

export interface GetTopupNumber {
  phone_number: string;
}

export interface GetAllTopupNumbers {
  include_deleted?: SoftDeleteFlag;
  /** Filter by linked platform payment type. */
  by_platform_payment_type?: string;
  cursor?: string;
  limit?: number;
}

export interface TopupNumbersPage {
  numbers: TopupNumber[];
  count?: number;
  cursor?: string;
}

export enum UpdateTopupNumberCommand {
  UPDATE_LOCATION = 'UPDATE_LOCATION',
  UPDATE_PLATFORM_PAYMENT_TYPE = 'UPDATE_PLATFORM_PAYMENT_TYPE',
  /** Update `operator` and/or `device_label`. */
  UPDATE_DEVICE = 'UPDATE_DEVICE',
  DELETE = 'DELETE',
}

export interface UpdateTopupNumber {
  phone_number: string;
  location?: string;
  password: string;
  platform_payment_type_id?: string;
  operator?: MessageOperator;
  device_label?: string;
  command: UpdateTopupNumberCommand;
}

/**
 * Promote a PENDING_SETUP number (synthetic `device:...` key) to its real
 * phone number. Idempotent: re-running moves any rows still left behind.
 */
export interface PromoteTopupNumber {
  /** The synthetic `device:<label>:<operator>` key. */
  pending_phone_number: string;
  /** The real phone number (digits, national format). */
  phone_number: string;
  /** Password for the new real number (used by the SMS reader app). */
  password: string;
  location?: string;
}

/**
 * Merge a PENDING_SETUP number into an existing Top-up Number: its money
 * in, money out and SMS move there, their operator becomes the number's,
 * its phone is assigned to the number and it is hidden. Idempotent.
 */
export interface MergeTopupNumber {
  /** The synthetic `device:<label>:<operator>` key. */
  pending_phone_number: string;
  /** An existing Top-up Number. */
  phone_number: string;
  /** Count only (the default); false applies the merge. */
  dry_run?: boolean;
  /** Also give the number the pending number's platform payment type. */
  use_pending_payment_type?: boolean;
}

export interface TopupNumberMergeResult {
  dry_run: boolean;
  pending_phone_number: string;
  phone_number: string;
  /** The phone (IFTTT device) now assigned to the number. */
  device_label?: string;
  /** Rows of the pending number (moved, or to move). */
  topup_requests: number;
  transfer_outs: number;
  wallet_messages: number;
  /** Rows whose operator differs from the number's (corrected). */
  operator_corrections: number;
  operator?: MessageOperator;
  /** The payment type the number gets (when asked and different). */
  platform_payment_type_id?: string;
}

export enum TopupDeviceStatus {
  /** Seen, not assigned: its transaction SMS are held (not stored). */
  UNASSIGNED = 'UNASSIGNED',
  /** Every SMS it forwards goes to `phone_number`. */
  ASSIGNED = 'ASSIGNED',
}

/** A phone (IFTTT DeviceLabel) that forwards SMS, and its Top-up Number. */
export interface TopupDevice extends Versioned {
  /** Trimmed, lower-case label: the key. */
  device_key: string;
  /** The label as first seen. */
  device_label: string;
  status: TopupDeviceStatus;
  /** ASSIGNED: the Top-up Number every SMS of this phone goes to. */
  phone_number?: string;
  assigned_at?: string;
  assigned_by_name?: string;
  first_seen_at: string;
  last_seen_at: string;
  /** IFTTT message ids held while UNASSIGNED (replayed on assignment). */
  held_message_ids?: string[];
  /** A number one of the held SMS states (pre-selected when assigning). */
  suggested_phone_number?: string;
  /**
   * When the admins were alerted about the unassigned phone. Optional;
   * missing = not yet (each held SMS retries the alert until it is sent).
   */
  alerted_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TopupDeviceDBItem extends TopupDevice {
  PK: string; // TOPUP_DEVICE#<device_key>
  SK: string; // TOPUP_DEVICE
  EntityIndexPK: string; // TOPUP_DEVICE
  EntityIndexSK: string; // DATE#<first_seen_at>
}

export interface TopupDevicesPage {
  devices: TopupDevice[];
}

/** Assign a phone to a Top-up Number (its future SMS go there). */
export interface AssignTopupDevice {
  device_label: string;
  phone_number: string;
}

/** Process an IFTTT-forwarded SMS (internal; used by the event consumer). */
export interface ProcessIftttSms {
  /** IftttSmsMessage.id — idempotency key for the raw message. */
  id: string;
  sender: string;
  sender_name?: string;
  message_content?: string;
  received_at?: string;
  device_name?: string;
  created_at: string;
}

export interface ProcessIftttSmsResult {
  /** IGNORED = not a mobile-money transaction. */
  /** HELD = the phone is not assigned to a Top-up Number yet. */
  outcome: 'CREATED' | 'DUPLICATE' | 'IGNORED' | 'NEEDS_REVIEW' | 'HELD';
  direction?: WalletTransferDirection;
  transaction_id?: string;
  receiver_number?: string;
}

/** One row of the per-number mobile-money ledger (money in + money out merged). */
export interface MobileMoneyLedgerEntry {
  transaction_id: string;
  receiver_number: string;
  direction: WalletTransferDirection.IN | WalletTransferDirection.OUT;
  operator?: MessageOperator;
  amount: number;
  fee: number;
  balance_after?: number;
  counterparty_number?: string;
  counterparty_name?: string;
  content?: string;
  message_content: string;
  /** ISO UTC. */
  occurred_at: string;
  created_at: string;
  subtype?: TxSubtype;
  reversed_by?: string;
  /** Money-in only. */
  claim_status?: ClaimStatus;
  merchant_id?: string;
  store_id?: string;
  claimed_at?: string;
  claimed_by?: string;
}

export interface GetMobileMoneyLedger {
  receiver_number: string;
  start_date?: string;
  end_date?: string;
  /** Max rows of each direction to read (default 200, max 1000). */
  limit?: number;
}

export interface MobileMoneyLedgerPage {
  entries: MobileMoneyLedgerEntry[];
  count: number;
}

export interface GetTopupNumberBalance {
  phone_number: string;
}

export interface ResetTopupPassword {
  phone_number: string;
}

export interface GetTopupsBalance {
  receiver_number: string;
  by_date_range?: boolean;
  start_date?: string;
  end_date?: string;
}

export interface GetTopupsBalanceResponse {
  total_in: number;
  total_out: number;
  calculated_balance: number;
  /** Latest balance reported by an SMS; falls back to `calculated_balance`. */
  real_balance: number;
  /** ISO time of the SMS that reported `real_balance`, when known. */
  real_balance_as_of?: string;
}

// ============================================================================
// Claim flow (new API exposed by service-topups)
// ============================================================================

/**
 * Direct claim by `transaction_id`. The caller knows the exact SMS-derived id
 * (typically copied from the customer's confirmation message).
 */
export interface ClaimTopupByTransactionId {
  transaction_id: string;
  merchant_id: string;
  store_id?: string;
  claimed_by: string;
  claimed_by_name?: string;
  entity_type: EntityTypes;
  /** Optional amount cross-check. Returns 422 when it mismatches the stored amount. */
  amount?: number;
  client_claim_text?: string;
  /**
   * The PaymentType.id to associate with the wallet credit.
   * Must belong to the merchant. Validated server-side before crediting.
   */
  payment_type_id: string;
}

/**
 * Fallback claim by amount + time window (+ optional narrowing fields) when the
 * customer cannot supply the `transaction_id`.
 */
export interface ClaimTopupByDetails {
  amount: number;
  transaction_time: string; // ISO
  merchant_id: string;
  store_id?: string;
  claimed_by: string;
  claimed_by_name?: string;
  entity_type: EntityTypes;
  /** Symmetric window in minutes around `transaction_time`. Default 30, max 120. */
  window_minutes?: number;
  sender_contact?: string;
  receiver_number?: string;
  operator?: MessageOperator;
  client_claim_text?: string;
  /**
   * The PaymentType.id to associate with the wallet credit.
   * Must belong to the merchant. Validated server-side before crediting.
   */
  payment_type_id: string;
}

/**
 * Claim a topup by pasting the original SMS message. The backend extracts
 * the transaction_id and amount (regex first, AI fallback), then delegates
 * to the standard claim-by-transaction-id flow.
 */
export interface ClaimTopupByMessage {
  /** The raw SMS message content to extract transaction details from. */
  message_content: string;
  merchant_id: string;
  store_id?: string;
  claimed_by: string;
  claimed_by_name?: string;
  entity_type: EntityTypes;
  client_claim_text?: string;
  /**
   * The PaymentType.id to associate with the wallet credit.
   * Must belong to the merchant. Validated server-side before crediting.
   */
  payment_type_id: string;
}

/**
 * Read-only matcher used by review UIs. Same selection logic as
 * `ClaimTopupByDetails` but never mutates and never calls the Wallets API.
 */
export interface FindTopupClaimMatches {
  amount: number;
  transaction_time: string;
  window_minutes?: number;
  sender_contact?: string;
  receiver_number?: string;
  operator?: MessageOperator;
  count?: number;
  cursor?: string;
}

export interface TopupClaimMatch {
  topup_request: TopupRequest;
  /** Human-readable reasons that led the matcher to surface this row. */
  reasons: string[];
}

export interface TopupClaimMatchesPage {
  matches: TopupClaimMatch[];
  count?: number;
  cursor?: string;
}

/** Manual review approval. Path-bound to the topup `transaction_id`. */
export interface ApproveTopupClaim {
  transaction_id: string;
  merchant_id: string;
  store_id?: string;
  approved_by: string;
  approved_by_name?: string;
  entity_type: EntityTypes;
  client_claim_text?: string;
  note?: string;
  /**
   * The PaymentType.id to associate with the wallet credit.
   * When not supplied the server reads it from the stored TopupRequest.
   */
  payment_type_id?: string;
}

/** Manual review rejection. Never calls the Wallets API. */
export interface RejectTopupClaim {
  transaction_id: string;
  rejected_by: string;
  entity_type: EntityTypes;
  reason: string;
  note?: string;
}

/**
 * Revoke a claimed topup: takes its credit back from the wallet it went to
 * (refused when that wallet no longer holds the amount) and marks it REVOKED.
 */
export interface RevokeTopupClaim {
  transaction_id: string;
  revoked_by: string;
  revoked_by_name?: string;
  entity_type: EntityTypes;
  reason: string;
  note?: string;
}

/** Put a REJECTED topup back to UNCLAIMED. Never calls the Wallets API. */
export interface ReopenTopupClaim {
  transaction_id: string;
  reopened_by: string;
  reopened_by_name?: string;
  entity_type: EntityTypes;
  note?: string;
}

/**
 * Correct what the SMS reader got wrong on an unclaimed, needs-review or
 * rejected topup. Only the fields sent change.
 */
export interface CorrectTopupRequest {
  transaction_id: string;
  corrected_by: string;
  corrected_by_name?: string;
  entity_type: EntityTypes;
  amount?: number;
  transaction_type?: TopupTransactionType;
  counterparty_name?: string;
  counterparty_number?: string;
  /** When the transaction happened (ISO UTC). */
  occurred_at?: string;
  content?: string;
  note?: string;
}

export interface GetTopupStatusCounts {
  merchant_id?: string;
  store_id?: string;
}

/** Number of topup requests per claim status. */
export interface TopupStatusCounts {
  counts: Record<ClaimStatus, number>;
  total: number;
}
